import React, { useState } from "react";
import { FileDown, GitBranch, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const DIRECTION_LABEL = {
    downstream: "Downstream · what this change breaks",
    upstream: "Upstream · what this change derives from",
    both: "Both directions",
};

/** Re-runs the blast-radius capture at a different depth/direction. The decision
 * is never touched — useful when the graph was built after the decision, or the
 * reviewer needs a wider picture for the audit file. */
function RecaptureControls({ rec, evidence, onUpdated }) {
    const [direction, setDirection] = useState(evidence.direction || "both");
    const [depth, setDepth] = useState(String(evidence.depth || 3));
    const [busy, setBusy] = useState(false);

    const recapture = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/recommendations/${rec.id}/impact-evidence/recapture`, {
                impact_direction: direction,
                impact_depth: Number(depth),
            });
            const captured = data?.impact_evidence?.status === "captured";
            toast[captured ? "success" : "warning"](
                captured
                    ? `Evidence re-captured · ${data.impact_evidence.impact?.summary?.impacted_nodes || 0} impacted objects`
                    : "Re-capture ran, but the graph still has no node for this recommendation",
            );
            onUpdated?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Re-capture failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Re-capture at</span>
            <select
                data-testid={TID.recommendationImpactDirection(rec.id)}
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                className="field text-[11px] py-1"
                style={{ width: "auto", paddingLeft: 8, paddingRight: 6 }}
                title="Traversal direction"
            >
                <option value="downstream">Downstream</option>
                <option value="upstream">Upstream</option>
                <option value="both">Both</option>
            </select>
            <select
                data-testid={TID.recommendationImpactDepth(rec.id)}
                value={depth}
                onChange={(e) => setDepth(e.target.value)}
                className="field text-[11px] py-1"
                style={{ width: "auto", paddingLeft: 8, paddingRight: 6 }}
                title="Traversal depth in hops"
            >
                {[1, 2, 3, 4, 5].map((d) => <option key={d} value={String(d)}>{`${d} hop${d > 1 ? "s" : ""}`}</option>)}
            </select>
            <button
                data-testid={TID.recommendationImpactRecapture(rec.id)}
                onClick={recapture}
                disabled={busy}
                className="btn-ghost inline-flex items-center gap-1.5 text-[11px] disabled:opacity-50"
            >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Re-capture evidence
            </button>
            {evidence.recapture_count ? (
                <span className="text-[10px] font-mono text-slate-400">
                    re-captured {evidence.recapture_count}× · decision unchanged
                </span>
            ) : null}
        </div>
    );
}

/** Blast-radius evidence captured the moment a recommendation was approved or
 * rejected. Every decided change carries its own downloadable Change Impact
 * Brief so an auditor can see exactly what was in scope at decision time. */
export function ImpactEvidencePanel({ rec, onUpdated }) {
    const [loading, setLoading] = useState(false);
    const evidence = rec?.impact_evidence;
    if (!evidence) return null;

    const download = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/recommendations/${rec.id}/impact-brief`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `change_impact_brief_${rec.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Change Impact Brief downloaded");
        } catch (e) {
            toast.error("Change Impact Brief unavailable");
        } finally {
            setLoading(false);
        }
    };

    if (evidence.status !== "captured") {
        return (
            <div
                data-testid={TID.recommendationImpactEvidence(rec.id)}
                className="text-[11px] text-amber-200/90 bg-amber-500/10 border border-amber-500/25 rounded-sm p-2.5 space-y-2"
            >
                <div className="flex items-start gap-2">
                    <ShieldAlert size={14} className="text-amber-400 shrink-0 mt-0.5" />
                    <span>
                        <strong className="text-amber-300">No impact evidence attached:</strong> {evidence.reason}
                    </span>
                </div>
                <RecaptureControls rec={rec} evidence={evidence} onUpdated={onUpdated} />
            </div>
        );
    }

    const summary = evidence.impact?.summary || {};
    const byType = summary.by_type || {};

    return (
        <div
            data-testid={TID.recommendationImpactEvidence(rec.id)}
            className="panel p-3 rounded-[4px] border border-cyan-500/20 space-y-2.5"
        >
            <div className="flex items-center gap-2 flex-wrap">
                <GitBranch size={13} className="text-cyan-400" />
                <span className="text-[11px] font-mono uppercase tracking-wider text-cyan-300">
                    Change Impact Brief · attached on {evidence.decision}
                </span>
                <span className="text-[10px] font-mono text-slate-400">
                    evidence {String(evidence.evidence_id).slice(0, 8)}
                </span>
            </div>
            <p className="text-[11px] text-slate-400">
                Blast radius of <span className="font-mono text-slate-200">{evidence.focus_label}</span> ·{" "}
                {DIRECTION_LABEL[evidence.direction] || evidence.direction} · {evidence.depth} hop(s) · captured{" "}
                {evidence.recaptured_at || evidence.generated_at}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-mono">
                <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                    {summary.impacted_nodes || 0} impacted objects
                </span>
                <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                    {summary.impacted_edges || 0} relations
                </span>
                <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                    depth reached {summary.max_depth_reached || 0}
                </span>
                {Object.entries(byType).map(([t, c]) => (
                    <span key={t} className="px-1.5 py-0.5 rounded-sm bg-violet-500/10 text-violet-300 border border-violet-500/25">
                        {String(t).replace("_", " ")} · {c}
                    </span>
                ))}
            </div>
            <button
                data-testid={TID.recommendationImpactBriefDownload(rec.id)}
                onClick={download}
                disabled={loading}
                className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
            >
                {loading ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                Download Change Impact Brief (PDF)
            </button>
            <RecaptureControls rec={rec} evidence={evidence} onUpdated={onUpdated} />
        </div>
    );
}
